import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { LocalOffer, SwapHoriz } from '@mui/icons-material';

export type PurchasedPackRef = {
  _id?: string;
  title?: string;
  packTitle?: string;
  classesLeft?: number | null;
  totalClasses?: number | null;
  expiryDate?: string;
  packEndDate?: string;
  startDate?: string;
  studio?: { studioName?: string; branchName?: string | null; _id?: string } | string;
  studioName?: string;
  pack?: { title?: string };
};

/** The booking's own location, so the tooltip can say when the pack is from elsewhere. */
export type BookingLocationRef = {
  _id?: string;
  studioName?: string;
  branchName?: string | null;
};

/**
 * How to name a location in a sentence: "Yelahanka branch", or the studio's
 * own name when it has no branch name — which is most studios. The word
 * "branch" must never be pinned onto a plain studio name.
 */
function locationPhrase(
  branchName?: string | null,
  studioName?: string | null,
): string | undefined {
  const b = branchName?.trim();
  if (b) return `${b} branch`;
  const n = studioName?.trim();
  return n || undefined;
}

const PACK_CHIP_SX = {
  backgroundColor: '#F4A7B9',
  color: '#8B1A35',
  fontWeight: 600,
  fontSize: '0.75rem',
} as const;

const tooltipPaperSx = {
  bgcolor: 'background.paper',
  color: 'text.primary',
  boxShadow: 4,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  p: 0,
  maxWidth: 320,
  '& .MuiTooltip-arrow': { color: 'background.paper' },
};

function formatPackDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatSessionsLeft(classesLeft: number | null | undefined, totalClasses: number | null | undefined) {
  if (classesLeft === null || classesLeft === undefined) return 'Unlimited sessions';
  if (totalClasses != null) return `${classesLeft} / ${totalClasses} sessions left`;
  return `${classesLeft} session${classesLeft !== 1 ? 's' : ''} left`;
}

export function getPurchasedPackTitle(pack?: PurchasedPackRef | null): string {
  if (!pack) return 'Pack';
  return pack.title || pack.packTitle || pack.pack?.title || 'Pack';
}

export function getPurchasedPackStudioName(pack?: PurchasedPackRef | null): string | undefined {
  if (!pack) return undefined;
  if (typeof pack.studio === 'object') {
    const phrase = locationPhrase(pack.studio?.branchName, pack.studio?.studioName);
    if (phrase) return phrase;
  }
  if (pack.studioName) return pack.studioName;
  return undefined;
}

/** The pack's own studio id, however the API shaped it. */
function packStudioId(pack?: PurchasedPackRef | null): string | undefined {
  if (!pack) return undefined;
  if (typeof pack.studio === 'string') return pack.studio;
  return pack.studio?._id;
}

/**
 * Was this booking paid from a pack bought at a DIFFERENT location?
 *
 * One definition, shared by the chip and the tooltip, so the badge on the row
 * and the explanation behind it can never disagree. Requires both ids: an
 * unknown location is not a difference.
 */
export function isCrossBranchPack(
  pack?: PurchasedPackRef | null,
  bookingLocation?: BookingLocationRef | null,
): boolean {
  const p = packStudioId(pack);
  const b = bookingLocation?._id;
  return Boolean(p && b && String(p) !== String(b));
}

/** The pack chip's own maroon, reused so the callout reads as part of it. */
const PACK_ACCENT = '#8B1A35';

export function hasPurchasedPackDetails(pack?: PurchasedPackRef | null): boolean {
  if (!pack) return false;
  return Boolean(
    pack._id ||
      pack.title ||
      pack.packTitle ||
      pack.pack?.title,
  );
}

function PackBookingTooltipContent({
  pack,
  bookingLocation,
}: {
  pack: PurchasedPackRef;
  bookingLocation?: BookingLocationRef | null;
}) {
  const title = getPurchasedPackTitle(pack);
  const studioName = getPurchasedPackStudioName(pack);
  const expiry = pack.expiryDate || pack.packEndDate;
  const sessionsLine = formatSessionsLeft(pack.classesLeft, pack.totalClasses);

  // ── DID THIS BOOKING SPEND ANOTHER BRANCH'S MEMBERSHIP? ──────────────────
  //
  // With "Packs work at every location" on, a booking at one branch can be
  // paid from a pack bought at another. The row itself gives no hint: the
  // Studio column names the SESSION's branch, and the Payment chip just says
  // "Pack Booking".
  //
  // This is the one place an operator can find out, which is why it is called
  // out rather than left as a second line of small print. Shown only when the
  // two are genuinely different AND both are known — never on the ordinary
  // same-branch booking, which is nearly all of them.
  const isCrossBranch = isCrossBranchPack(pack, bookingLocation);
  const bookedAt = locationPhrase(
    bookingLocation?.branchName,
    bookingLocation?.studioName,
  );

  return (
    <Box sx={{ p: 1.5, minWidth: 220, maxWidth: 300 }}>
      <Box display="flex" alignItems="center" gap={0.75} mb={1}>
        <LocalOffer sx={{ fontSize: 16, color: '#8B1A35' }} />
        <Typography variant="subtitle2" fontWeight={700}>
          Pack used
        </Typography>
      </Box>
      <Typography variant="body2" fontWeight={600} lineHeight={1.35}>
        {title}
      </Typography>
      {studioName && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          Bought at {studioName}
        </Typography>
      )}
      {isCrossBranch && (
        <Box
          mt={0.9}
          px={1}
          py={0.7}
          sx={{
            borderRadius: 1.5,
            // The pack chip's own maroon at low opacity — this is a fact about
            // the pack, not a warning. Amber read as "something went wrong"
            // when nothing had: spending a shared pack is exactly what the
            // brand switched on.
            bgcolor: `${PACK_ACCENT}0F`,
            border: `1px solid ${PACK_ACCENT}33`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.7,
          }}
        >
          <SwapHoriz sx={{ fontSize: 15, mt: '1px', color: PACK_ACCENT }} />
          <Typography
            variant="caption"
            sx={{ color: PACK_ACCENT, fontWeight: 600, lineHeight: 1.35 }}
          >
            Different branch{bookedAt ? ` — booked at ${bookedAt}` : ''}
          </Typography>
        </Box>
      )}
      <Typography variant="caption" fontWeight={600} display="block" mt={0.75} sx={{ color: '#8B1A35' }}>
        {sessionsLine}
      </Typography>
      {expiry && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.35}>
          Expires {formatPackDate(expiry)}
        </Typography>
      )}
      {pack.startDate && new Date(pack.startDate) > new Date() && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.35}>
          Starts {formatPackDate(pack.startDate)}
        </Typography>
      )}
    </Box>
  );
}

type PackBookingTooltipProps = {
  pack?: PurchasedPackRef | null;
  /** The booking's own location — lets the tooltip flag a cross-branch pack. */
  bookingLocation?: BookingLocationRef | null;
  children: React.ReactElement;
};

export function PackBookingTooltip({ pack, bookingLocation, children }: PackBookingTooltipProps) {
  if (!hasPurchasedPackDetails(pack)) {
    return children;
  }

  return (
    <Tooltip
      arrow
      enterDelay={300}
      title={<PackBookingTooltipContent pack={pack!} bookingLocation={bookingLocation} />}
      slotProps={{ tooltip: { sx: tooltipPaperSx } }}
    >
      {children}
    </Tooltip>
  );
}

type PackPaymentChipProps = {
  pack?: PurchasedPackRef | null;
  bookingLocation?: BookingLocationRef | null;
  label?: string;
  size?: 'small' | 'medium';
  sx?: Record<string, unknown>;
};

/** Pack payment chip with hover details — for booking list payment columns. */
export function PackPaymentChip({
  pack,
  bookingLocation,
  label = 'Pack Booking',
  size = 'small',
  sx,
}: PackPaymentChipProps) {
  // ── VISIBLE WITHOUT HOVERING ─────────────────────────────────────────────
  //
  // The tooltip explains it, but nobody hovers every row. Scanning a day's
  // bookings, the one paid from another branch's membership has to be findable
  // by eye — so the chip itself carries the same ⇄ mark the tooltip uses, and
  // the two come from one `isCrossBranchPack` so a badge can never appear
  // without its explanation.
  //
  // Same maroon as the chip text, not a second colour: it is a qualifier on
  // "Pack Booking", not a separate status.
  const crossBranch = isCrossBranchPack(pack, bookingLocation);

  const chip = (
    <Chip
      label={label}
      size={size}
      icon={
        crossBranch ? (
          <SwapHoriz
            sx={{ fontSize: 15, color: `${PACK_ACCENT} !important` }}
          />
        ) : undefined
      }
      sx={{
        ...PACK_CHIP_SX,
        ...(crossBranch
          ? {
              border: `1px solid ${PACK_ACCENT}55`,
              '& .MuiChip-icon': { ml: 0.6, mr: -0.35 },
            }
          : {}),
        ...sx,
      }}
    />
  );

  return (
    <PackBookingTooltip pack={pack} bookingLocation={bookingLocation}>
      <Box component="span" display="inline-flex">
        {chip}
      </Box>
    </PackBookingTooltip>
  );
}

/** TypeChip-style pack label with hover details (UserDetail / StudioCustomers tables). */
export function PackPaymentTypeChip({
  pack,
  config,
}: {
  pack?: PurchasedPackRef | null;
  config: Record<string, { label: string; color: string }>;
}) {
  const cfg = config.subscription;
  const chip = (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: `${cfg.color}15`,
        color: cfg.color,
        border: `1px solid ${cfg.color}40`,
        fontWeight: 500,
        fontSize: '0.7rem',
      }}
    />
  );

  return (
    <PackBookingTooltip pack={pack}>
      <Box component="span" display="inline-flex">
        {chip}
      </Box>
    </PackBookingTooltip>
  );
}
